/* eslint-disable no-param-reassign */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { CircularProgress } from '@mui/material'
import clsx from 'clsx'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import Selectable from 'selectable.js'
import cleanDeep from 'clean-deep'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { withStyles } from '~/themes/useStyles'
import { dialogParams } from '~/components/common/Dialog/CustomDialog'
import { showDialog } from '~/components/common/Dialog'
import Filter from './Filter'
import { useMedia } from '~/redux/hooks/media'
import {
  MEDIA_FOLDER_STATIC,
  MEDIA_FILE_TYPE,
  MEDIA_FILE_MODE,
  PARENT_MODE,
  INITIAL_FILTERS_DATA,
} from '~/constants/media'
import style from './style'
import { isFileImage, substringFileName } from '~/utils/helpers/media'
import { TextBlockToolTip } from '~components/common/TextBlock'
import useDrag from '~/utils/hooks/media/useDrag'
import ImageGrayBase64 from './ImageGrayBase64'
import { convertLocalToGmtTime } from '~/utils/datetime'
import useMediaScroll from '~/utils/hooks/useMediaScroll'
import GridView from './GridView'
import ListView from './ListView'
import { useUser } from '~/redux/hooks/user'
import Button from '~/components/common/Button'
import Icon from '~/components/common/Icon'
import { COLORS } from '~/themes/common'
import mediaApis from '~/services/apis/media'

const Files = (props) => {
  const {
    classes,
    folderSelectedId: folderId,
    fileSelected,
    setFileSelected,
    multiFileSelect,
    openFile,
    show,
    select,
    onSelectedFolder,
    onBulkSelect,
    bulkSelect,
    parentMode,
    filters,
    setFilters,
    fileMode,
    setFileMode,
  } = props
  const { mediaFile, mediaFolder, actions, loading } = useMedia()
  const { orgId } = useUser()

  const galleryMode = parentMode.includes(PARENT_MODE.GALLERY)
  const [showBulkDelete, setShowBulkDelete] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const folderSelectedId = useMemo(
    () => folderId || MEDIA_FOLDER_STATIC.ALL_FILE,
    [folderId],
  )

  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.down(1165))

  const isFileSelected = (file) =>
    fileSelected &&
    fileSelected.some((fileSelect) => fileSelect.src === file.src)

  const folderSelected = useMemo(() => {
    const newFolder = mediaFolder.list.find((f) => f.id === folderSelectedId)
    if (folderSelectedId === MEDIA_FOLDER_STATIC.ALL_FILE) {
      return { id: MEDIA_FOLDER_STATIC.ALL_FILE, name: 'All Files' }
    }

    if (folderSelectedId === MEDIA_FOLDER_STATIC.UNSORTED) {
      return { id: MEDIA_FOLDER_STATIC.UNSORTED, name: 'Unsorted Files' }
    }

    return newFolder
  }, [mediaFolder, folderSelectedId])

  const showVideo = show.some((e) => e === MEDIA_FILE_TYPE.VIDEO)
  const selectVideo = select.some(
    (e) => e === MEDIA_FILE_TYPE.ALL || e === MEDIA_FILE_TYPE.VIDEO,
  )
  const showImage = show.some((e) => e === MEDIA_FILE_TYPE.IMAGE)
  const selectImage = select.some(
    (e) => e === MEDIA_FILE_TYPE.ALL || e === MEDIA_FILE_TYPE.IMAGE,
  )

  // selectable
  const selectableRef = useRef()

  // drag
  const dragRef = useRef()

  const { scrollHeight, clientHeight, scrollTop, setScrollTop } =
    useMediaScroll()

  const files = useMemo(() => {
    if (showVideo) {
      return mediaFile.list.filter((file) => !isFileImage(file))
    }
    if (showImage) {
      return mediaFile.list.filter((file) => isFileImage(file))
    }
    return mediaFile.list
  }, [mediaFile])

  const fetchListFile = ({ params, total_pages, listFile }) => {
    actions.setListFileFolderData({
      page: params.page,
      total_pages,
      files: listFile || [],
    })

    if (params?.filters?.created_from) {
      params.filters.created_from = convertLocalToGmtTime(
        params.filters.created_from,
      )
    }

    if (params?.filters?.created_to) {
      params.filters.created_to = convertLocalToGmtTime(
        params.filters.created_to,
      )
    }

    params.limit = isTablet ? 28 : 72
    actions.getListFile(cleanDeep(params))
  }

  useDrag({
    ref: dragRef,
    onDragStart: (event) => {
      const dragLayerEl = document.createElement('div')
      const countEl = document.createElement('div')
      const fileNameEl = document.createElement('div')
      const imgEl = document.createElement('img')

      dragLayerEl.className = 'dragLayerContainer'
      countEl.className = 'dragLayerCount'
      fileNameEl.className = 'dragLayerFileName'

      imgEl.src = ImageGrayBase64
      imgEl.alt = 'image_gray'
      imgEl.width = 24
      imgEl.height = 24
      imgEl.style.marginLeft = '8px'

      countEl.innerHTML = selectableRef.current?.getSelectedItems().length
      fileNameEl.innerHTML = substringFileName(
        selectableRef.current
          ?.getSelectedItems()[0]
          .node.getAttribute('data-name'),
      )
      dragLayerEl.appendChild(imgEl)
      dragLayerEl.appendChild(fileNameEl)
      dragLayerEl.appendChild(countEl)

      document.body.appendChild(dragLayerEl)
      const fileSelect = selectableRef.current
        .getSelectedItems()
        .map((e) => e.node.getAttribute('data-id'))
        .toString()
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.dropEffect = 'all'
      event.dataTransfer.setDragImage(dragLayerEl, 0, 0)
      event.dataTransfer.setData('fileSelected', fileSelect)

      selectableRef.current?.getSelectedItems().forEach((element) => {
        element.node.style.opacity = 0.5
      })
    },
    onDragEnd: () => {
      const dragLayerEl = document.getElementsByClassName('dragLayerContainer')
      document.body.removeChild(dragLayerEl[0])
      selectableRef.current?.getSelectedItems().forEach((el) => {
        el.node.style.opacity = ''
      })
      selectableRef.current.clear()
      setShowBulkDelete(false)
    },
  })

  useEffect(() => {
    const newFilter = { ...cleanDeep(filters) }
    delete newFilter.sort
    const params = {
      folder_id: folderSelectedId,
      limit: mediaFile.limit,
      page: 1,
      filters: newFilter,
      sort: filters.sort,
    }

    if (selectableRef.current) {
      selectableRef.current.destroy()
      setShowBulkDelete(false)
    }
    if (folderSelectedId && orgId) {
      setScrollTop(0)
      fetchListFile({ params, total_pages: 0 })
    }
  }, [folderSelectedId, orgId, filters])

  useEffect(() => {
    if (
      !loading.getListFile &&
      mediaFile.total_pages &&
      mediaFile.page + 1 <= mediaFile.total_pages &&
      scrollTop !== 0 &&
      scrollHeight - clientHeight - scrollTop < 25
    ) {
      const params = {
        folder_id: folderSelectedId,
        limit: mediaFile.limit,
        page: mediaFile.page + 1,
        filters: cleanDeep(filters),
        sort: filters.sort,
      }
      fetchListFile({
        params,
        total_pages: mediaFile.total_pages,
        listFile: mediaFile.list,
      })
    }
  }, [scrollHeight, clientHeight, scrollTop, mediaFile, filters])

  useEffect(() => {
    const container = document.getElementById('mediaFileContainer')
    if (selectableRef.current && isTablet) {
      selectableRef.current.clear()
      selectableRef.current.refresh()
      selectableRef.current.destroy()
    }
    if (container && mediaFile.list.length > 0 && !galleryMode && !isTablet) {
      if (selectableRef.current) {
        selectableRef.current.clear()
        selectableRef.current.refresh()
      }
      selectableRef.current = new Selectable({
        appendTo: container,
        ignore: '.ui-selected',
        lasso: {
          border: '1px solid #75798A',
          borderRadius: '5px',
          backgroundColor: 'rgb(0 0 0 / 10%)',
        },
      })
      selectableRef.current.add(container.children)
      selectableRef.current.on('end', (e, selected) => {
        setShowBulkDelete(selected.length !== 0)
      })
    }
  }, [selectableRef, mediaFile, isTablet, fileMode])

  useEffect(() => {
    const handleKey = (event) => {
      const { key } = event
      if (key === 'Escape' && selectableRef.current) {
        selectableRef.current.clear()
        selectableRef.current.refresh()
        setShowBulkDelete(false)
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('keydown', handleKey)
    }
  }, [selectableRef])

  useEffect(() => {
    if (
      !isTablet &&
      (loading.moveFile === 'success' || loading.moveFile === 'failure')
    ) {
      selectableRef.current.clear()
      selectableRef.current.refresh()
      selectableRef.current.update()
    }
  }, [loading, selectableRef, isTablet])

  const handleSelectFile = (file, fileIdx) => {
    if (isTablet && !bulkSelect && !galleryMode) return openFile(fileIdx)
    if (!fileSelected) return false
    if (multiFileSelect) {
      if (isFileSelected(file)) {
        const newFileSelected = fileSelected.filter(
          (fileSelect) => fileSelect.src !== file.src,
        )
        return setFileSelected(newFileSelected)
      }

      const newFileSelected = [...fileSelected, file]
      return setFileSelected(newFileSelected)
    }
    return setFileSelected(isFileSelected(file) ? [] : [file])
  }

  const handleOpenFile = useCallback(
    (fileIdx) => {
      if (!galleryMode) return openFile(fileIdx)
      return false
    },
    [galleryMode],
  )

  const handleApplyFilter = useCallback(
    (values) => {
      setFilters({ ...filters, ...values })
    },
    [folderSelectedId],
  )

  const handleClearFilter = useCallback(() => {
    const defaultFilterData = { ...INITIAL_FILTERS_DATA }
    if (showVideo || showImage) {
      delete defaultFilterData.content_type
      setFilters({ ...filters, ...defaultFilterData })
    } else {
      setFilters(INITIAL_FILTERS_DATA)
    }
  }, [folderSelectedId, filters])

  const handleSort = useCallback(
    (sortId, nextArrange) => {
      const nextSort = {}
      if (nextArrange !== 0) {
        nextSort[sortId] = nextArrange
      }

      setFilters({ ...filters, ...{ sort: nextSort } })
    },
    [folderSelectedId, filters],
  )

  const handleChangeFileMode = useCallback(
    (val) => {
      setFileMode(val)
      setShowBulkDelete(false)
    },
    [showBulkDelete, folderSelectedId, setFileMode, setShowBulkDelete],
  )

  const handleDelete = () => {
    const fileIds = selectableRef.current
      ?.getSelectedItems()
      .map((e) => e.node.getAttribute('data-id'))
    showDialog({
      ...dialogParams.deleteFile,
      onOk: async () => {
        try {
          setDeleteLoading(true)
          await Promise.all(
            Array.from(fileIds).map(async (id) =>
              mediaApis.deleteFile({ file_id: id }),
            ),
          )
          await actions.setListFileFolderData({
            page: 1,
            total_pages: 0,
          })
          await actions.getListFolder()
          await actions.getListFile(
            cleanDeep({
              folder_id: folderSelectedId,
              limit: mediaFile.limit,
              page: 1,
              filters,
              sort: filters.sort,
            }),
          )
        } finally {
          setDeleteLoading(false)
          setShowBulkDelete(false)
        }
      },
    })
  }

  const backFolder = () => {
    const parentFolder = mediaFolder.list.find(
      (f) => f.id === folderSelected.parent_id,
    )
    onSelectedFolder(parentFolder)
  }

  const renderBackButton = () => (
    <div className={classes.backContainer}>
      {folderSelected?.parent_id && folderSelected?.parent_id !== 0 ? (
        <KeyboardArrowLeftIcon
          style={{ cursor: 'pointer' }}
          fontSize="large"
          color="primary"
          onClick={backFolder}
        />
      ) : (
        ''
      )}
      <div className={classes.backTitle}>
        <TextBlockToolTip
          title={
            <span>
              {`${folderSelected?.name || ''} (${loading.getListFile ? '' : mediaFile.total_items})`}
            </span>
          }
          maxLine={1}
        >
          <div
            data-tooltip
            className={classes.folderTitleDetail}
          >{`${Object.keys(cleanDeep(filters)).length !== 0 ? 'Results' : folderSelected?.name || ''} (${mediaFile.total_items})`}</div>
        </TextBlockToolTip>
      </div>
    </div>
  )

  const renderFirstLoading = () =>
    loading.getListFile && mediaFile.total_pages === 0 ? (
      <div className={classes.loadingContainer}>
        <CircularProgress size={30} />
      </div>
    ) : (
      ''
    )

  return (
    <div className={clsx(classes.container, galleryMode && 'galleryMode')}>
      {renderBackButton()}
      <Filter
        filters={filters}
        mode={fileMode}
        setMode={setFileMode}
        onApplyFilter={handleApplyFilter}
        onClearFilter={handleClearFilter}
        onChangeFileMode={handleChangeFileMode}
        onBulkSelect={onBulkSelect}
        bulkSelect={bulkSelect}
        galleryMode={galleryMode}
        showImage={showImage}
        showVideo={showVideo}
      />
      {galleryMode ? '' : <div className={classes.divider} />}
      <div
        className={clsx(classes.bulkActionContainer, {
          [classes.collapse]: !showBulkDelete || isTablet,
        })}
      >
        {showBulkDelete && !isTablet ? (
          <>
            <div>Bulk Action</div>
            <Button
              color="third"
              onClick={handleDelete}
              loading={deleteLoading}
            >
              <Icon name="trash" color={COLORS.accent.rubyRed1} />
              <span className={classes.deleteTitle}>Delete</span>
            </Button>
          </>
        ) : (
          ''
        )}
      </div>
      <div
        id="mediaListFile"
        ref={galleryMode || isTablet ? null : dragRef}
        className={clsx(
          classes.filesContainer,
          galleryMode && 'galleryMode',
          'customized-scrollbar',
        )}
      >
        {fileMode === MEDIA_FILE_MODE.GRID_VIEW ? (
          <GridView
            files={files}
            fileSelected={fileSelected}
            selectVideo={selectVideo}
            selectImage={selectImage}
            loading={loading}
            mediaFile={mediaFile}
            isFileSelected={isFileSelected}
            handleOpenFile={handleOpenFile}
            handleSelectFile={handleSelectFile}
            bulkSelect={bulkSelect}
            galleryMode={galleryMode}
          />
        ) : (
          <ListView
            files={files}
            onSort={handleSort}
            sortBy={filters.sort}
            loading={loading}
            mediaFile={mediaFile}
            isFileSelected={isFileSelected}
            handleOpenFile={handleOpenFile}
            handleSelectFile={handleSelectFile}
            fileSelected={fileSelected}
            selectVideo={selectVideo}
            selectImage={selectImage}
            bulkSelect={bulkSelect}
          />
        )}
      </div>
      {renderFirstLoading()}
    </div>
  )
}

Files.defaultProps = {
  fileSelected: null,
  multiFileSelect: true,
  setFileSelected: () => {},
  onSelectedFolder: () => {},
  openFile: () => {},
  show: [MEDIA_FILE_TYPE.ALL],
  select: [MEDIA_FILE_TYPE.ALL],
  folderSelectedId: '',
  onBulkSelect: () => {},
  bulkSelect: false,
  filters: {},
  fileMode: '',
  setFileMode: () => {},
  setFilters: () => {},
}

Files.propTypes = {
  classes: PropTypes.shape().isRequired,
  folderSelectedId: PropTypes.string,
  multiFileSelect: PropTypes.bool,
  parentMode: PropTypes.oneOfType([PropTypes.array, PropTypes.shape])
    .isRequired,
  fileSelected: PropTypes.oneOfType([PropTypes.array, PropTypes.shape()]),
  setFileSelected: PropTypes.func,
  openFile: PropTypes.func,
  onSelectedFolder: PropTypes.func,
  show: PropTypes.oneOfType([PropTypes.array, PropTypes.shape()]),
  select: PropTypes.oneOfType([PropTypes.array, PropTypes.shape()]),
  filters: PropTypes.oneOfType([PropTypes.object, PropTypes.shape()]),
  onBulkSelect: PropTypes.func,
  bulkSelect: PropTypes.bool,
  setFileMode: PropTypes.func,
  fileMode: PropTypes.string,
  setFilters: PropTypes.func,
}

export default withStyles(style)(Files)
